import { useEffect, useRef } from 'react';

/**
 * Keep a value in sessionStorage for as long as it stands, and put it back
 * when the page comes up again under the same key — so a reload (onto a newer
 * deploy, stale-bundle-reload, or any other) loses nothing typed or picked.
 *
 * Mirrored on every change rather than saved at the moment of the reload
 * (2026-09-25): reloads can come back to back while a deploy switches over,
 * and a page that reloads again before it has drawn what it restored would
 * have nothing to save. `serialized` is '' when there is nothing to keep;
 * `restore` receives what was kept. Until what was restored has been drawn
 * (serialized is no longer ''), the stored copy is left alone.
 *
 * `dropOnLeave` (release check 2026-10-27): the copy is for a reload only, so
 * leaving the page inside the app lets it go — a reload never unmounts the
 * page, so it still finds it. Without it, coming back later put an abandoned
 * edit in place of what the server holds (New friends: Edit opened on the
 * topics they had cleared and walked away from, Next greyed out, while their
 * card had six). A trip to sign in (/auth) keeps it: it comes back here. Off
 * by default — a chat's unsent words and photos are meant to wait for the
 * next visit.
 */
export function useKeptAcrossReload(
  key: string | null,
  serialized: string,
  restore: (kept: string) => void,
  { dropOnLeave = false }: { dropOnLeave?: boolean } = {},
): void {
  const restoring = useRef<string | null>(null);
  const restoreRef = useRef(restore);
  restoreRef.current = restore;
  const leaving = useRef({ key, dropOnLeave });
  leaving.current = { key, dropOnLeave };

  // On unmount only (the key read from the ref): StrictMode's mount-unmount-mount in development runs this before
  // the page has a key, so it lets go of nothing.
  useEffect(() => () => {
    const { key: left, dropOnLeave: drop } = leaving.current;
    if (!drop || !left) return;
    if (window.location.pathname === '/auth') return;
    try { window.sessionStorage.removeItem(left); } catch { /* nothing kept */ }
  }, []);

  useEffect(() => {
    restoring.current = null;
    if (!key) return;
    try {
      const kept = window.sessionStorage.getItem(key);
      if (!kept) return;
      restoring.current = key;
      restoreRef.current(kept);
    } catch { /* nothing to put back */ }
  }, [key]);

  useEffect(() => {
    if (!key) return;
    if (restoring.current === key) {
      if (!serialized) return;
      restoring.current = null;
    }
    try {
      if (serialized) window.sessionStorage.setItem(key, serialized);
      else window.sessionStorage.removeItem(key);
    } catch { /* kept in memory only, as before */ }
  }, [key, serialized]);
}
